// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, useMemo, type ReactNode } from 'react';

import { useGeneralContext } from 'helpers/useGeneralContext';

import { MachineElementActionsModalProvider } from './modalRegistry';
import { useBendQA, type BendQARoute } from './useBendQA';
import { useDoUndoMachineElement } from './useDoUndoMachineElement';

type MachineElementActionsContextType = ReturnType<typeof useDoUndoMachineElement> & ReturnType<typeof useBendQA>;

const MachineElementActionsContext = createContext<MachineElementActionsContextType | undefined>(undefined);

type Props = {
  /** The route this provider is mounted on. Its `bendId` search param drives the modal. */
  route: BendQARoute;
  children: ReactNode;
};

/**
 * Provides context for machine element actions, including marking elements as done or undone (aka unfinished) and handling Bend QA.
 */
const MachineElementActionsProviderInner = ({ route, children }: Props) => {
  const doUndoValue = useDoUndoMachineElement();
  const bendQAValue = useBendQA({ route, onMarkBendDone: doUndoValue.markElementDone });

  const value = useMemo(
    () => ({
      ...doUndoValue,
      ...bendQAValue,
    }),
    [bendQAValue, doUndoValue],
  );

  return <MachineElementActionsContext value={value}>{children}</MachineElementActionsContext>;
};

/** Wrapper that combines both {@link MachineElementActionsModalProvider} and {@link MachineElementActionsProviderInner} */
export const MachineElementActionsProvider = ({ route, children }: Props) => (
  <MachineElementActionsModalProvider>
    <MachineElementActionsProviderInner route={route}>{children}</MachineElementActionsProviderInner>
  </MachineElementActionsModalProvider>
);

/**
 * Used to perform machine element actions, including marking elements as done/undone and handling Bend QA.
 * Requires a {@link MachineElementActionsProvider} above it.
 **/
export const useMachineElementActions = () => useGeneralContext(MachineElementActionsContext, 'MachineElementActions');
